import { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import { api } from "../api";
import ProductCard from "../components/ProductCard";
import { SectionReveal } from "../components/Reveal";

const CATEGORIES = ["All", "Outerwear", "Apparel", "Accessories", "Bottoms"];

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [category, setCategory] = useState("All");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("featured");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (category !== "All") params.category = category;
    if (q.trim()) params.q = q.trim();
    api
      .get("/products", { params })
      .then((r) => setProducts(r.data))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [category, q]);

  const sorted = useMemo(() => {
    const list = [...products];
    if (sort === "low") list.sort((a, b) => a.price - b.price);
    if (sort === "high") list.sort((a, b) => b.price - a.price);
    return list;
  }, [products, sort]);

  return (
    <main data-testid="shop-page" className="pt-16 min-h-screen">
      <div className="px-4 sm:px-8 lg:px-16 py-14 border-b border-zinc-900">
        <p className="mono text-xs tracking-[0.25em] text-[#FF334B] uppercase">Catalog</p>
        <h1 className="font-display font-extrabold tracking-tighter uppercase text-4xl sm:text-5xl lg:text-6xl mt-3">
          The Archive
        </h1>
      </div>

      <div className="px-4 sm:px-8 lg:px-16 py-6 flex flex-col lg:flex-row lg:items-center gap-5 border-b border-zinc-900 sticky top-16 bg-[#0A0A0C]/90 backdrop-blur-xl z-30">
        <div className="flex flex-wrap gap-2">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              data-testid={`category-pill-${c.toLowerCase()}`}
              onClick={() => setCategory(c)}
              className={`mono text-[10px] tracking-[0.2em] uppercase px-4 py-2 border transition-colors ${
                category === c ? "border-[#FF334B] text-[#FF334B]" : "border-zinc-700 text-zinc-400 hover:border-zinc-500"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
        <div className="flex-1" />
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            data-testid="shop-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="SEARCH PIECES"
            className="bg-transparent border border-zinc-700 pl-9 pr-4 py-2 mono text-xs tracking-[0.15em] placeholder:text-zinc-600 focus:outline-none focus:border-[#FF334B] w-full lg:w-56"
          />
        </div>
        <select
          data-testid="shop-sort-select"
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          className="bg-[#0A0A0C] border border-zinc-700 px-3 py-2 mono text-xs tracking-[0.15em] uppercase text-zinc-400 focus:outline-none focus:border-[#FF334B]"
        >
          <option value="featured">Featured</option>
          <option value="low">Price: Low to High</option>
          <option value="high">Price: High to Low</option>
        </select>
      </div>

      <div className="px-4 sm:px-8 lg:px-16 py-14">
        {loading ? (
          <p data-testid="shop-loading" className="mono text-xs tracking-[0.25em] text-zinc-500 uppercase">Loading the archive…</p>
        ) : sorted.length === 0 ? (
          <p data-testid="shop-empty-message" className="text-zinc-500">No pieces match your search.</p>
        ) : (
          <div data-testid="shop-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {sorted.map((p, i) => (
              <SectionReveal key={p.slug} delay={(i % 3) * 0.08}>
                <ProductCard product={p} />
              </SectionReveal>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
